import React, { useEffect, useMemo, useState } from 'react';

import makeStyles from '@mui/styles/makeStyles';
import { Box, Typography, Card, CardContent, Button } from '@mui/material';

import CompleteOrder from './CompleteOrder';
import CompleteExteriorSignangeSubmisson from './CompleteExteriorSignangeSubmisson';
import CompleteMerchandisingProgram from './CompleteMerchandisingProgram';
import { useSelector } from 'react-redux';
import { useActiveCart } from 'services/Data/CartService';
import { useDealer } from 'services/Data/DealerServices';
import { useActiveMerchandisingProgramQuote } from 'services/Data/MerchandisingProgramQuoteService';
import { useActiveExteriorSignageQuote } from 'services/Data/ExteriorSignageQuoteService';
import { useTranslation } from 'react-i18next';
import { IsClub, IsImpersonationMode } from 'config/config';
import QuickDealerChangeModal from '@acnb/CustomComponents/DealerCard/QuickDealerChangeModal';
import ClubDealerVerificationModal from './ClubDealerVerificationModal';
import {
  hasAnyActiveDealerWork,
  hasActiveCartWork,
  hasActiveExteriorSignageQuoteWork,
  hasActiveMerchandisingProgramQuoteWork,
} from 'services/Utilities/dealerWorkUtils';
import { useHistory } from 'react-router-dom';

const useStyles = makeStyles(theme => ({
  pageFull: {
    width: '100%',
  },
  noItem: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
    minHeight: 'calc(100vh - 240px)',
    padding: theme.spacing(3),
    width: '100%',
  },
  emptyStateCard: {
    width: '100%',
    maxWidth: 680,
    borderRadius: 16,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: theme.shadows[5],
    textAlign: 'center',
  },
  emptyStateActions: {
    marginTop: theme.spacing(3),
    display: 'flex',
    justifyContent: 'center',
  },
  cartFooter: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
}));

const Cart = () => {
  const classes = useStyles();
  const accountReducer = useSelector(state => state.AccountReducer);
  const { t } = useTranslation();
  const history = useHistory();
  const dealerId = accountReducer?.profile?.dealer_Id;
  const [showClubDealerVerification, setShowClubDealerVerification] = useState(false);
  const [showClubDealerSwitchModal, setShowClubDealerSwitchModal] = useState(false);
  const { data: dealer } = useDealer();

  const { data: cart } = useActiveCart(accountReducer.isLoggedIn, false, IsImpersonationMode ? dealerId : null);
  const { data: merchandisingProgramQuote } = useActiveMerchandisingProgramQuote(accountReducer.isLoggedIn);
  const { data: exteriorSignageQuote } = useActiveExteriorSignageQuote(accountReducer.isLoggedIn);

  const dealerName = dealer?.customerName || t('dealer');
  const dealerNumber = dealer?.identifier;
  const dealerPhone = dealer?.phoneNumber;
  const dealerLocation = [dealer?.city, dealer?.region?.internalName, dealer?.postalCode].filter(Boolean).join(', ');

  const hasActiveCartArtifacts = useMemo(
    () =>
      hasAnyActiveDealerWork({
        cart,
        merchandisingProgramQuote,
        exteriorSignageQuote,
      }),
    [cart, exteriorSignageQuote, merchandisingProgramQuote],
  );

  const copyPayload = useMemo(
    () => ({
      cart,
      exteriorSignageQuote,
      merchandisingProgramQuote,
    }),
    [cart, exteriorSignageQuote, merchandisingProgramQuote],
  );

  useEffect(() => {
    if (!IsClub || !hasActiveCartArtifacts) {
      return;
    }

    setShowClubDealerVerification(true);
  }, [hasActiveCartArtifacts]);

  return (
    <Box className={classes.pageFull} sx={{ mt: '-53px' }}>
      <ClubDealerVerificationModal
        show={showClubDealerVerification}
        onConfirmCurrentDealer={() => setShowClubDealerVerification(false)}
        onSwitchDealer={() => {
          setShowClubDealerVerification(false);
          setShowClubDealerSwitchModal(true);
        }}
      />

      <QuickDealerChangeModal
        show={showClubDealerSwitchModal}
        onClose={() => setShowClubDealerSwitchModal(false)}
        switchMode="cartConfirmation"
        copyPayload={copyPayload}
      />

      {hasActiveCartWork(cart) ? <CompleteOrder /> : ''}
      {hasActiveExteriorSignageQuoteWork(exteriorSignageQuote) ? <CompleteExteriorSignangeSubmisson /> : ''}

      {hasActiveMerchandisingProgramQuoteWork(merchandisingProgramQuote) ? <CompleteMerchandisingProgram /> : ''}

      {!hasActiveMerchandisingProgramQuoteWork(merchandisingProgramQuote) &&
        !hasActiveExteriorSignageQuoteWork(exteriorSignageQuote) &&
        !hasActiveCartWork(cart) && (
          <Box className={classes.noItem}>
            <Box sx={{ width: '100%', maxWidth: 860, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Card className={classes.emptyStateCard} sx={{ mx: 'auto' }}>
                <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
                  <Typography variant="h2" sx={{ mb: 1.5 }}>
                    {t('emptyCart.title')}
                  </Typography>
                  {IsClub ? (
                    <Box
                      sx={{
                        mb: 3,
                      }}>
                      <Box
                        sx={{
                          border: '1px solid #eceef3',
                          borderRadius: 2,
                          backgroundColor: '#f7f9fc',
                          p: 1.5,
                          textAlign: 'left',
                          maxWidth: 560,
                          mx: 'auto',
                          mb: 1.5,
                        }}>
                        <Typography
                          sx={{ fontSize: 12, fontWeight: 700, color: 'text.secondary', mb: 0.75, textAlign: 'center' }}>
                          {t('verifyDealerCart')}
                        </Typography>
                        <Typography sx={{ fontSize: 16, fontWeight: 700, color: 'text.primary', lineHeight: 1.25 }}>
                          {dealerName}
                          {dealerNumber ? ` #${dealerNumber}` : ''}
                        </Typography>
                        {dealerLocation ? (
                          <Typography sx={{ mt: 0.5, fontSize: 13, color: 'text.secondary' }}>
                            {t('address')}: {dealerLocation}
                          </Typography>
                        ) : null}
                        {dealerPhone ? (
                          <Typography sx={{ mt: 0.25, fontSize: 13, color: 'text.secondary' }}>
                            {t('appModule.phone')}: {dealerPhone}
                          </Typography>
                        ) : null}
                      </Box>

                      <Button
                        variant="contained"
                        color="secondary"
                        disableElevation
                        sx={{ px: 4, py: 1.25, borderRadius: 0, color: '#000', fontWeight: 700 }}
                        onClick={() => setShowClubDealerSwitchModal(true)}>
                        {t('notYourDealer')}
                      </Button>
                    </Box>
                  ) : null}

                  <Box
                    sx={{
                      pt: IsClub ? 2.5 : 0,
                      borderTop: IsClub ? theme => `1px solid ${theme.palette.divider}` : 'none',
                    }}>
                    <Typography color="text.secondary" sx={{ maxWidth: 520, mx: 'auto' }}>
                      {t('emptyCart.description')}
                    </Typography>
                    <Box className={classes.emptyStateActions}>
                      <Button
                        variant="contained"
                        color="secondary"
                        disableElevation
                        sx={{ px: 4, py: 1.25, borderRadius: 1, color: '#000' }}
                        onClick={() => history.push('/dealer')}>
                        {t('keepShopping')}
                      </Button>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Box>
          </Box>
        )}
    </Box>
  );
};

export default Cart;
